"use client";

import { useCallback, useMemo, useState } from "react";
import type {
  SourceField,
  SourceValues,
} from "@/components/tool-kit/source-citation-builder";
import { CitationForm } from "@/components/tools/special-sources/citation-form";
import { useToolUi } from "@/components/tools/tool-ui";
import {
  FIGURE_FIELDS as F,
  type FigureLabels,
  type FigureSourceType,
  figureIsReady,
  formatFigure,
} from "@/lib/tool-kit/special-sources/figure-citation-generator";

/** English strings. A translated content file overrides these through `ui`. */
export const FIGURE_CITATION_GENERATOR_STRINGS = {
  formHeading: "The figure and its source",
  resultsHeading: "Your number, title and credit",
  emptyPrompt:
    "Enter the figure's number and the title of its source to see your note, caption and credit line.",
  copy: "Copy",
  copied: "Copied!",
  tryExample: "Try an example",
  clear: "Clear",
  objectKindLabel: "You are reproducing a",
  objectFigure: "Figure (graph, chart, photo, map, drawing)",
  objectTable: "Table",
  numberLabel: "Its number in your paper",
  numberPlaceholder: "1",
  figureTitleLabel: "Your title for it",
  figureTitlePlaceholder: "Mean regression slopes in experiment 1",
  figureTitleHint:
    "Type it in sentence case. APA's title case is applied for you; Chicago keeps it as typed.",
  noteTextLabel: "Other note text (optional, APA)",
  noteTextPlaceholder: "Error bars represent standard errors.",
  useLabel: "Did you change it?",
  useReprinted: "No — reprinted exactly",
  useAdapted: "Yes — adapted (redrawn, cropped, relabeled, shortened)",
  sourceTypeLabel: "It comes from a",
  sourceArticle: "Journal article",
  sourceBook: "Book or report",
  sourceChapter: "Chapter in an edited book",
  sourceWebpage: "Webpage",
  authorsLabel: "Authors, one per line",
  authorsPlaceholder: "Xiaoye Michael Wang\nMats Lind\nGeoffrey P. Bingham",
  authorsHint:
    "First name then surname. A group such as U.S. Census Bureau is kept whole.",
  yearLabel: "Year published",
  yearPlaceholder: "2018",
  sourceTitleLabel: "Title of the article, book, chapter or webpage",
  sourceTitlePlaceholder:
    "Large continuous perspective change with noncoplanar points enables accurate slant perception",
  sourceTitleHint:
    "In sentence case. Each style's capitalization is applied for you.",
  journalLabel: "Journal",
  journalPlaceholder:
    "Journal of Experimental Psychology: Human Perception and Performance",
  bookTitleLabel: "Title of the book the chapter is in",
  siteLabel: "Website name (leave blank if the author runs the site)",
  editorsLabel: "Editors of the book, one per line",
  editionLabel: "Edition (optional)",
  editionPlaceholder: "6th",
  volumeLabel: "Volume",
  volumePlaceholder: "44",
  issueLabel: "Issue",
  issuePlaceholder: "10",
  pageLabel: "Page the figure is on",
  pagePlaceholder: "1513",
  publisherLabel: "Publisher",
  linkLabel: "DOI or URL",
  linkPlaceholder: "10.1037/xhp0000553",
  statusLabel: "Copyright status",
  statusCopyright: "Copyrighted",
  statusCc: "Creative Commons licence",
  statusPublic: "Public domain",
  copyrightYearLabel: "Copyright year",
  copyrightYearPlaceholder: "2018",
  copyrightHolderLabel: "Copyright holder",
  copyrightHolderPlaceholder: "the American Psychological Association",
  permissionLabel:
    "I have permission from the copyright holder to reprint or adapt it",
  licenseLabel: "Licence",
  apaLabel: "APA 7th edition",
  mlaLabel: "MLA 9th edition",
  chicagoLabel: "Chicago 18th edition",
  apaNumber: "Number (bold, above the title)",
  apaTitle: "Title (italic title case, below the number)",
  apaNote: "Note (below the figure or table)",
  mlaCaption: "Caption (below the figure)",
  mlaTableLabel: "Label (above the table)",
  mlaTableTitle: "Title (on the next line)",
  mlaTableSource: "Source line (below the table)",
  chicagoCaption: "Caption with credit line (below the figure)",
  chicagoTableTitle: "Number and title (above the table)",
  chicagoTableSource: "Source note (below the table)",
  apaGuide:
    "The source still needs a reference list entry. Write Reprinted with permission or Adapted with permission only if you obtained it.",
  mlaGuide:
    "A caption that gives the source in full needs no Works Cited entry, unless you also cite the source in your text.",
  chicagoGuide:
    "Cite the source in a note and the bibliography too if you discuss it in your text.",
  example: {
    objectKind: "figure",
    number: "3",
    figureTitle: "Mean regression slopes in experiment 1",
    noteText: "Error bars represent standard errors.",
    use: "reprinted",
    sourceType: "article",
    authors: "Xiaoye Michael Wang\nMats Lind\nGeoffrey P. Bingham",
    year: "2018",
    sourceTitle:
      "Large continuous perspective change with noncoplanar points enables accurate slant perception",
    container:
      "Journal of Experimental Psychology: Human Perception and Performance",
    volume: "44",
    issue: "10",
    page: "1513",
    link: "10.1037/xhp0000553",
    status: "copyright",
    copyrightYear: "2018",
    copyrightHolder: "the American Psychological Association",
  },
} as const;

type Strings = typeof FIGURE_CITATION_GENERATOR_STRINGS;

const LICENSES = [
  "CC BY 4.0",
  "CC BY-SA 4.0",
  "CC BY-NC 4.0",
  "CC BY-NC-SA 4.0",
  "CC BY-ND 4.0",
  "CC BY-NC-ND 4.0",
] as const;

function sourceFields(strings: Strings, type: FigureSourceType): SourceField[] {
  const page: SourceField = {
    id: F.page,
    kind: "text",
    label: strings.pageLabel,
    placeholder: strings.pagePlaceholder,
  };
  const publisher: SourceField = {
    id: F.publisher,
    kind: "text",
    label: strings.publisherLabel,
  };
  const edition: SourceField = {
    id: F.edition,
    kind: "text",
    label: strings.editionLabel,
    placeholder: strings.editionPlaceholder,
  };
  if (type === "article") {
    return [
      {
        id: F.container,
        kind: "text",
        label: strings.journalLabel,
        placeholder: strings.journalPlaceholder,
        wide: true,
      },
      {
        id: F.volume,
        kind: "text",
        label: strings.volumeLabel,
        placeholder: strings.volumePlaceholder,
      },
      {
        id: F.issue,
        kind: "text",
        label: strings.issueLabel,
        placeholder: strings.issuePlaceholder,
      },
      page,
    ];
  }
  if (type === "book") {
    return [publisher, edition, page];
  }
  if (type === "chapter") {
    return [
      {
        id: F.container,
        kind: "text",
        label: strings.bookTitleLabel,
        wide: true,
      },
      { id: F.editors, kind: "people", label: strings.editorsLabel },
      publisher,
      edition,
      page,
    ];
  }
  return [
    { id: F.container, kind: "text", label: strings.siteLabel, wide: true },
  ];
}

function statusFields(strings: Strings, status: string): SourceField[] {
  if (status === "cc") {
    return [
      {
        id: F.license,
        kind: "select",
        label: strings.licenseLabel,
        options: LICENSES.map((license) => ({
          value: license,
          label: license,
        })),
      },
    ];
  }
  if (status === "public") {
    return [];
  }
  return [
    {
      id: F.copyrightYear,
      kind: "text",
      label: strings.copyrightYearLabel,
      placeholder: strings.copyrightYearPlaceholder,
    },
    {
      id: F.copyrightHolder,
      kind: "text",
      label: strings.copyrightHolderLabel,
      placeholder: strings.copyrightHolderPlaceholder,
    },
    {
      id: F.permission,
      kind: "checkbox",
      label: strings.permissionLabel,
      wide: true,
    },
  ];
}

function buildFields(strings: Strings, values: SourceValues): SourceField[] {
  const type = (values[F.sourceType] || "article") as FigureSourceType;
  return [
    {
      id: F.objectKind,
      kind: "select",
      label: strings.objectKindLabel,
      options: [
        { value: "figure", label: strings.objectFigure },
        { value: "table", label: strings.objectTable },
      ],
    },
    {
      id: F.number,
      kind: "text",
      label: strings.numberLabel,
      placeholder: strings.numberPlaceholder,
    },
    {
      id: F.figureTitle,
      kind: "text",
      label: strings.figureTitleLabel,
      placeholder: strings.figureTitlePlaceholder,
      hint: strings.figureTitleHint,
      wide: true,
    },
    {
      id: F.noteText,
      kind: "text",
      label: strings.noteTextLabel,
      placeholder: strings.noteTextPlaceholder,
      wide: true,
    },
    {
      id: F.use,
      kind: "select",
      label: strings.useLabel,
      options: [
        { value: "reprinted", label: strings.useReprinted },
        { value: "adapted", label: strings.useAdapted },
      ],
    },
    {
      id: F.sourceType,
      kind: "select",
      label: strings.sourceTypeLabel,
      options: [
        { value: "article", label: strings.sourceArticle },
        { value: "book", label: strings.sourceBook },
        { value: "chapter", label: strings.sourceChapter },
        { value: "webpage", label: strings.sourceWebpage },
      ],
    },
    {
      id: F.authors,
      kind: "people",
      label: strings.authorsLabel,
      placeholder: strings.authorsPlaceholder,
      hint: strings.authorsHint,
    },
    {
      id: F.year,
      kind: "text",
      label: strings.yearLabel,
      placeholder: strings.yearPlaceholder,
    },
    {
      id: F.sourceTitle,
      kind: "text",
      label: strings.sourceTitleLabel,
      placeholder: strings.sourceTitlePlaceholder,
      hint: strings.sourceTitleHint,
      wide: true,
    },
    ...sourceFields(strings, type),
    {
      id: F.link,
      kind: "text",
      label: strings.linkLabel,
      placeholder: strings.linkPlaceholder,
    },
    {
      id: F.status,
      kind: "select",
      label: strings.statusLabel,
      options: [
        { value: "copyright", label: strings.statusCopyright },
        { value: "cc", label: strings.statusCc },
        { value: "public", label: strings.statusPublic },
      ],
    },
    ...statusFields(strings, values[F.status] ?? "copyright"),
  ];
}

/** The widget's strings, as the formatter's labels. */
export function figureLabels(strings: Strings): FigureLabels {
  return {
    apa: strings.apaLabel,
    mla: strings.mlaLabel,
    chicago: strings.chicagoLabel,
    apaNumber: strings.apaNumber,
    apaTitle: strings.apaTitle,
    apaNote: strings.apaNote,
    mlaCaption: strings.mlaCaption,
    mlaTableLabel: strings.mlaTableLabel,
    mlaTableTitle: strings.mlaTableTitle,
    mlaTableSource: strings.mlaTableSource,
    chicagoCaption: strings.chicagoCaption,
    chicagoTableTitle: strings.chicagoTableTitle,
    chicagoTableSource: strings.chicagoTableSource,
    apaGuide: strings.apaGuide,
    mlaGuide: strings.mlaGuide,
    chicagoGuide: strings.chicagoGuide,
  };
}

export default function FigureCitationGeneratorWidget() {
  const strings = useToolUi(FIGURE_CITATION_GENERATOR_STRINGS);
  const [values, setValues] = useState<SourceValues>({});
  const fields = useMemo(() => buildFields(strings, values), [strings, values]);
  const labels = useMemo(() => figureLabels(strings), [strings]);
  const outputs = useMemo(
    () => (figureIsReady(values) ? formatFigure(values, labels) : []),
    [values, labels]
  );
  const update = useCallback((id: string, value: string) => {
    setValues((current) => ({ ...current, [id]: value }));
  }, []);

  return (
    <CitationForm
      fields={fields}
      onChange={update}
      onClear={() => setValues({})}
      onExample={() => setValues({ ...strings.example })}
      outputs={outputs}
      strings={strings}
      values={values}
    />
  );
}
